import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { oauthApi } from '../../services/api';
import styles from './Auth.module.css';

export const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  
  const login = useAuthStore((state) => state.login);
  const oauthConfig = useAuthStore((state) => state.oauthConfig);
  const fetchOAuthConfig = useAuthStore((state) => state.fetchOAuthConfig);
  const handleOAuthCallback = useAuthStore((state) => state.handleOAuthCallback);
  const navigate = useNavigate();

  // Fetch OAuth config on mount
  useEffect(() => {
    fetchOAuthConfig();
  }, [fetchOAuthConfig]);

  // Handle OAuth callback (token in URL) or error
  useEffect(() => {
    const token = searchParams.get('token');
    const oauthError = searchParams.get('oauth_error');

    if (oauthError) {
      setError(decodeURIComponent(oauthError));
      // Clean up URL
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (token) {
      // Handle OAuth callback
      handleOAuthCallback(token)
        .then(() => {
          navigate('/');
        })
        .catch((err) => {
          setError('OAuth login failed. Please try again.');
          console.error('OAuth callback error:', err);
        })
        .finally(() => {
          // Clean up URL
          window.history.replaceState({}, '', '/login');
        });
    }
  }, [searchParams, handleOAuthCallback, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = () => {
    // Redirect to OAuth authorization endpoint
    window.location.href = oauthApi.getAuthorizeUrl();
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBox}>
        <h1 className={styles.authTitle}>Welcome Back</h1>
        <p className={styles.authSubtitle}>Login to manage your docs</p>

        {/* OAuth Login Button */}
        {oauthConfig?.enabled && (
          <>
            <div className={styles.oauthSection}>
              <button
                type="button"
                className={styles.oauthButton}
                onClick={handleOAuthLogin}
              >
                <span className={styles.oauthIcon}>🔐</span>
                Login with {oauthConfig.providerName}
              </button>
            </div>
            <div className={styles.oauthDivider}>
              <span className={styles.oauthDividerText}>or</span>
            </div>
          </>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Username</label>
            <input
              type="text"
              className={styles.formInput}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Password</label>
            <input
              type="password"
              className={styles.formInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && <div className={styles.formError}>{error}</div>}

          <button
            type="submit"
            className={styles.formButton}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className={styles.formLink}>
          Don't have an account? <Link to="/register">Register</Link>
        </div>
      </div>
    </div>
  );
};
